import type {
  InstanceCustomisationView,
  InstanceDetail,
  InstanceSummary,
  ItemStatusEvent,
  NotificationSubscriptionDto,
  PortfolioResponse,
  SegmentStatusEvent,
} from "@keydials/api-client";

// The subset of the api client the dial load needs — kept explicit so the orchestration below is unit
// testable without the whole client (or a DOM).
export interface WorkspaceLoadDeps {
  listMyInstances: (token: string) => Promise<PortfolioResponse>;
  getInstance: (token: string, id: string, organisationId?: string) => Promise<InstanceDetail>;
  getInstanceHistory: (token: string, id: string, organisationId?: string) => Promise<SegmentStatusEvent[]>;
  getInstanceItemHistory: (token: string, id: string, organisationId?: string) => Promise<ItemStatusEvent[]>;
  // These two take no org argument: they ride the api client's active org, which is set before they go out.
  getInstanceCustomisation: (token: string, id: string) => Promise<InstanceCustomisationView>;
  listSubscriptions: (token: string, id: string) => Promise<NotificationSubscriptionDto[]>;
  setActiveOrg: (organisationId: string | null) => void;
}

/** What the page already knows about where this dial lives, cheapest source first. */
export interface WorkspaceLoadHints {
  /** The org the navigation carried: My Dials, a group's tile, a drill-in and the breadcrumb back all pass it. */
  navOrgId: string | null;
  /** Reached by drilling into a Group Key Dial's child, which is never in the portfolio. */
  drillIn?: boolean;
  /** Resolved by an earlier load of THIS dial (null included). Undefined when it never has been. */
  knownOrgId?: string | null;
  /** The portfolio as an earlier load on this page fetched it, when one has. */
  portfolio?: readonly InstanceSummary[];
}

/**
 * Every read the workspace renders from, each its own promise so the caller can paint the dial the moment
 * `detail` lands and fold the rest in as they arrive. All of them go out together once the owning org is
 * known — none waits on another.
 */
export interface WorkspaceLoad {
  /** The org every read below was scoped to. Never rejects. */
  owningOrg: Promise<string | null>;
  /** The one gating read: rejects when the dial cannot be opened (404, a KEY-434 428, a failure). */
  detail: Promise<InstanceDetail>;
  /** Best-effort, like the three below: a failure resolves to an empty timeline (KEY-221). */
  history: Promise<SegmentStatusEvent[]>;
  itemHistory: Promise<ItemStatusEvent[]>;
  /** Null when it could not be read — a definition with customisation off still opens. */
  customisation: Promise<InstanceCustomisationView | null>;
  /** Null when it could not be read; keep what you have. */
  subscriptions: Promise<NotificationSubscriptionDto[] | null>;
  /** The portfolio's dials, when this load asked for them at all (null inside: refused or failed). */
  portfolio: Promise<InstanceSummary[] | null> | null;
}

// Why the portfolio is here at all. A dial's owning org may differ from the token's active org (cross-org
// access, KEY-132), and the server deliberately insists on the explicit X-KeyDials-Org header before it will
// honour a cross-org grant on a TOP-LEVEL dial. On a bare URL (a bookmark, a notification, a reload) nothing
// else names that org, and the cross-org portfolio does. A Group Key Dial's children are never in it — they
// are reachable only by drilling in, and the server resolves their org from the parent chain (KEY-221). The
// portfolio is also the only source of the dial's summary: the organisation and definition names the dial
// bar and the Overview title show (KEY-458), which the instance detail does not carry.
//
// It used to be awaited on EVERY dial change, before the detail was even requested, although navigation had
// almost always named the org already. It is the heaviest request on the path — a full card per visible dial
// — so opening a group paid for it and then for its own rollup, one after the other. Now:
//   - an org the page already has (a reload, a portfolio seen earlier on this page, or navigation state) is
//     used at once and every read goes out immediately;
//   - a drill-in never asks, since a child cannot be in the portfolio;
//   - with only a nav org, the portfolio is still asked for, but only for the summary, and nothing waits on it;
//   - only a cold deep link with nothing else to go on waits for it, to name the org.
export function startWorkspaceLoad(
  deps: WorkspaceLoadDeps,
  token: string,
  id: string,
  hints: WorkspaceLoadHints,
): WorkspaceLoad {
  const { navOrgId, drillIn = false, knownOrgId, portfolio: seen } = hints;
  const seenMatch = seen?.find((s) => s.id === id);

  // Best-effort, deliberately: a CONFINED session (kiosk PIN, guest — KEY-297) is refused the cross-org
  // portfolio outright by InstanceScopeMiddleware, and an ordinary session can hit a transient failure
  // here. Neither may sink the dial itself — without a portfolio there is simply no resolved org, and the
  // server then resolves the owning org from the dial's own parent chain (KEY-221), exactly as it does
  // for a bookmarked group child. Before this catch, a guest's workspace hung on "Loading…" forever.
  const askPortfolio = knownOrgId === undefined && !seenMatch && !drillIn && !(seen && navOrgId);
  const portfolio = askPortfolio
    ? deps.listMyInstances(token).then((p) => p.instances, () => null)
    : null;

  const resolved: string | null | undefined =
    knownOrgId !== undefined ? knownOrgId
      : seenMatch ? seenMatch.organisationId
        : navOrgId || drillIn ? navOrgId
          : undefined;

  const readsUnder = (org: string | null) => {
    deps.setActiveOrg(org);
    const scope = org ?? undefined;
    return {
      detail: deps.getInstance(token, id, scope),
      history: deps.getInstanceHistory(token, id, scope).catch(() => [] as SegmentStatusEvent[]),
      itemHistory: deps.getInstanceItemHistory(token, id, scope).catch(() => [] as ItemStatusEvent[]),
      customisation: deps.getInstanceCustomisation(token, id).catch(() => null),
      subscriptions: deps.listSubscriptions(token, id).catch(() => null),
    };
  };

  if (resolved !== undefined) {
    // Synchronously, so the reads leave in the same tick rather than a microtask later.
    return { owningOrg: Promise.resolve(resolved), ...readsUnder(resolved), portfolio };
  }

  // Cold deep link: the portfolio names the org (a miss means a child, or a refusal — no scope either way).
  const owningOrg = portfolio!.then((list) => list?.find((s) => s.id === id)?.organisationId ?? null);
  const reads = owningOrg.then(readsUnder);
  return {
    owningOrg,
    detail: reads.then((r) => r.detail),
    history: reads.then((r) => r.history),
    itemHistory: reads.then((r) => r.itemHistory),
    customisation: reads.then((r) => r.customisation),
    subscriptions: reads.then((r) => r.subscriptions),
    portfolio,
  };
}

/**
 * Latest-wins bookkeeping for the workspace's loads, across reloads of one dial AND across dials.
 *
 * The page is not remounted when the URL moves from one dial to another (group → child, the breadcrumb back),
 * so a response can arrive for a dial that is no longer the one in the URL. A sequence number alone let one
 * through whenever nothing newer had begun, and an old dial's debounced reload — or the reload a write
 * awaits — could still START after navigation and supersede the new dial's own load.
 */
export interface LoadSequencer {
  /** Record the dial now in the URL. Anything begun for another dial stops being current. */
  show(id: string | undefined): void;
  /** Whether `id` is still the dial in the URL. */
  isLive(id: string | undefined): boolean;
  /**
   * Begin a load of `id`. Null when `id` is not the dial in the URL — the caller does nothing, so it can
   * never supersede that dial's load. Otherwise a check that holds until a newer load begins or the dial changes.
   */
  begin(id: string): (() => boolean) | null;
}

export function createLoadSequencer(initialId?: string): LoadSequencer {
  let live = initialId;
  let seq = 0;
  return {
    show(id) {
      live = id;
    },
    isLive(id) {
      return id !== undefined && id === live;
    },
    begin(id) {
      if (id !== live) {
        return null;
      }
      const mine = ++seq;
      return () => mine === seq && id === live;
    },
  };
}
